import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import PageSection from 'components/PageSection/PageSection';
import { withMainLayout } from 'hocs';
import PageTitle from 'components/AtomicStructure/atoms/PageTitle';
import NewWrapper from 'components/AtomicStructure/layouts/NewWrapper';
import Error from 'components/AtomicStructure/atoms/Error';
import ButtonsBlockRow from 'components/ButtonsBlockRow';
import UploadDocuments from 'components/UploadDocuments/UploadDocuments';

import { newPersonalInformationOnboardingLink, questionPageLink } from 'pages/ClientOnboardingFlow/helpers/links';
import { getUserRegisterId } from 'pages/ClientOnboardingFlow/helpers/userRegisterId';
import { getBase64FromFile } from 'utils/file';
import ServiceManager from 'services/ServiceManager';

import { MAXALLOWEDFILES } from './validation/const';
import './UploadDocumentsPage.css';

// Interface for the document payload sent to the API
interface DocumentUploadItem {
    filename: string;
    file: string; // Base64 string without the prefix
}

interface UploadPayload {
    ownerId: string | number | null;
    documents: DocumentUploadItem[];
}

const UploadDocumentsPage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();

    const userRegisterId = getUserRegisterId();

    // State with explicit types
    const [files, setFiles] = useState<File[]>([]);
    const [isSending, setSending] = useState<boolean>(false);
    const [errorPage, setErrorPage] = useState<string>('');

    const goBack = (): void => {
        navigate(`${newPersonalInformationOnboardingLink}/id=${userRegisterId}`);
    };

    const goToAction = useCallback(async (): Promise<void> => {
        try {
            if (files?.length) {
                setSending(true);

                // Process files into the required Base64 format
                const documents: DocumentUploadItem[] = await Promise.all(
                    files.map(async (file: File) => {
                        const base64Data = await getBase64FromFile(file);

                        return {
                            filename: file.name,
                            // Split the base64 string to remove the "data:image/png;base64," prefix
                            file: base64Data.split(',')[1],
                        };
                    }),
                );

                const payload: UploadPayload = {
                    ownerId: userRegisterId,
                    documents,
                };

                await ServiceManager.customDocumentsService('uploadIdDocument', [payload]);
            }

            navigate(questionPageLink);
        } catch (err: any) {
            setErrorPage(err?.message || 'An unexpected error occurred');
        } finally {
            setSending(false);
        }
    }, [files, userRegisterId, navigate]);

    return (
        <NewWrapper
            stepNavBarActive={1}
            headTitle={t('onboardingFlow.customerOnboarding.title')}
        >
            <PageSection className="newStyle">
                <div className="newStyleContent client-onboarding--upload-documents">
                    <div className="container">
                        <PageTitle className="textCenter">
                            {t('onboardingFlow.documetsPage.pleaseFollow')}
                        </PageTitle>

                        <p className="text-center text-light text-large text-dark">
                            {t('onboardingFlow.documetsPage.description')
                                .replace('{maxUploadCount}', String(MAXALLOWEDFILES))}
                        </p>

                        <UploadDocuments
                            onError={(val: string | null) => setErrorPage(val as string)}
                            onSuccess={(uploadedFiles: File[]) => setFiles(uploadedFiles)}
                        />
                    </div>

                    <div className="JourneyFooter">
                        <div className="container">
                            {errorPage && <Error>{errorPage}</Error>}

                            <ButtonsBlockRow
                                additionalButton={{
                                    text: t('onboardingFlow.documetsPage.back'),
                                    onClick: goBack,
                                }}
                                primaryButton={{
                                    text: t('onboardingFlow.clientsDataPage.continue'),
                                    onClick: goToAction,
                                    loading: isSending,
                                }}
                            />
                        </div>
                    </div>
                </div>
            </PageSection>
        </NewWrapper>
    );
};

export default withMainLayout(UploadDocumentsPage);
